import { LabeledValue } from 'aqTypes';
import {
  ClassifiedReference,
  EnrichedReference,
  Entry,
  EntryValue,
  GroupEntryProps,
  SingleEntryProps,
} from './types';
import { APIReferenceAttributes, APIReferenceType } from '@ardoq/api-types';

const singleEntryProps = ({
  sourceComponentName,
  targetComponentName,
  referenceTypeName,
}: EnrichedReference): SingleEntryProps | null => {
  if (!sourceComponentName || !targetComponentName || !referenceTypeName) {
    return null;
  }

  return {
    sourceComponentName,
    targetComponentName,
    referenceType: referenceTypeName,
  };
};

const groupEntryProps = (
  enriched: EnrichedReference[]
): GroupEntryProps | null => {
  const { referenceTypeName, sourceWorkspaceName } = enriched[0];

  if (!referenceTypeName || !sourceWorkspaceName) {
    return null;
  }

  return {
    sourceWorkspaceName,
    referenceType: referenceTypeName,
    numberOfReferences: enriched.length,
  };
};

export const buildEntry = (enriched: EnrichedReference[]): Entry | null => {
  const props =
    enriched.length === 1
      ? singleEntryProps(enriched[0])
      : groupEntryProps(enriched);

  const { validOptions } = enriched[0];

  if (!props || validOptions.length === 0) {
    return null;
  }

  return {
    validOptions,
    enrichedReferences: enriched,
    props,
  };
};

export type ClassifyReferenceOpts = {
  referenceTypeName: string | undefined;
  targetWsReferenceTypes: Record<string, APIReferenceType>;
};

export const classifyReference = (
  attributes: APIReferenceAttributes,
  { referenceTypeName, targetWsReferenceTypes }: ClassifyReferenceOpts
): ClassifiedReference => {
  const base = { reference: attributes, referenceTypeName };

  if (attributes.rootWorkspace === attributes.targetWorkspace) {
    return { ...base, equivalentType: attributes.type };
  }

  const equallyNamedTypeID = Object.keys(targetWsReferenceTypes).find(
    key => targetWsReferenceTypes[key].name === referenceTypeName
  );

  if (equallyNamedTypeID) {
    return { ...base, equivalentType: Number(equallyNamedTypeID) };
  }

  return { ...base, equivalentType: null };
};

export const isGroupEntryProps = (
  entryProps: GroupEntryProps | SingleEntryProps
): entryProps is GroupEntryProps => {
  return (entryProps as GroupEntryProps).numberOfReferences !== undefined;
};

export const getIntersectionOfReferenceOptions = (
  entries: Entry[]
): LabeledValue[] => {
  const referenceOptions = entries.map(e => e.validOptions.map(o => o.label));
  return referenceOptions[0]
    .filter(value => referenceOptions.every(e => e.includes(value)))
    .map((label, value) => ({
      label,
      value,
    }));
};

export const getEntryValues = (
  entries: Entry[],
  label?: string
): EntryValue[] =>
  entries.map(e => ({
    referenceIds: e.enrichedReferences.map(({ reference }) => reference._id),
    typeValue: label
      ? e.validOptions.find(e => e.label === label)!.value
      : null,
  }));
